import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaSave, FaCopy } from 'react-icons/fa';
import { useTheme } from '../ThemeContext';
import StaffStatus from './StaffStatus';

// Global Admin → Microsoft Sign-In. Platform-level Entra ID (Azure AD) SSO config used for
// the "Sign in with Microsoft" button on the login page. Multi-tenant app: any customer's
// Microsoft work account can sign in; users are mapped to a tenant by email domain.
function EntraSettings() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sso, setSso] = useState({
    enabled: false,
    has_client_secret: false,
    masked_client_secret: '',
    entra_client_id: '',
    entra_authority: 'organizations'
  });
  const [clientSecret, setClientSecret] = useState('');

  const adminToken = localStorage.getItem('Admintoken');
  const redirectUri = `${window.location.origin}/s1/api/auth/entra/callback`;

  const loadSso = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/s1/admin/sso-settings', {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load SSO settings');
      if (data.settings) setSso(data.settings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSso = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s1/admin/sso-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          enabled: sso.enabled,
          clientId: sso.entra_client_id || '',
          authority: sso.entra_authority || 'organizations',
          // Only send the secret when the admin typed a new value (blank = keep existing).
          clientSecret: clientSecret.trim() || undefined
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save SSO settings');
      setSso(data.settings);
      setClientSecret('');
      setMessage('Microsoft sign-in settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyRedirect = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(redirectUri);
    setMessage('Redirect URI copied to clipboard.');
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const isConfigured = sso.enabled && sso.has_client_secret && sso.entra_client_id;

  return (
    <div className={isDarkMode ? 'text-light' : ''}>
      <StaffStatus on={isConfigured}>
        {isConfigured ? 'Enabled' : 'Disabled'}
      </StaffStatus>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Card className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Card.Body>
          <Row className="g-4">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Application (client) ID</Form.Label>
                <Form.Control
                  value={sso.entra_client_id || ''}
                  onChange={(e) => setSso(prev => ({ ...prev, entra_client_id: e.target.value }))}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  From your Entra app registration → Overview.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Client Secret</Form.Label>
                <Form.Control
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={sso.has_client_secret ? `Saved (${sso.masked_client_secret || '••••'}). Enter a new secret to replace it.` : 'Client secret value'}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  From Entra → Certificates &amp; secrets. Stored encrypted server-side; never returned to the browser.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Supported Accounts</Form.Label>
                <Form.Select
                  value={sso.entra_authority || 'organizations'}
                  onChange={(e) => setSso(prev => ({ ...prev, entra_authority: e.target.value }))}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                >
                  <option value="organizations">Work/school accounts (any org)</option>
                  <option value="common">Work/school + personal accounts</option>
                </Form.Select>
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  "Any org" is recommended — personal accounts can't map to a tenant domain.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Redirect URI (add this in Entra)</Form.Label>
                <div className="d-flex">
                  <Form.Control
                    readOnly
                    value={redirectUri}
                    className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                  />
                  <Button variant="outline-secondary" className="ms-2" onClick={copyRedirect} title="Copy">
                    <FaCopy />
                  </Button>
                </div>
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  Add this exact URL as a Web redirect URI in your Entra app registration.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Check
                type="switch"
                id="entra-sso-enabled"
                label="Enable Sign in with Microsoft on the login page"
                checked={!!sso.enabled}
                onChange={(e) => setSso(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button onClick={saveSso} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
              Save Microsoft Sign-In Settings
            </Button>
          </div>

          <div className={`mt-4 small ${isDarkMode ? 'text-light opacity-75' : 'text-muted'}`}>
            <strong>Setup in the Microsoft Entra admin center:</strong>
            <ol className="mb-0 mt-2 ps-3">
              <li>App registrations → New registration → supported account types: <em>Accounts in any organizational directory (multi-tenant)</em>.</li>
              <li>Platform: <em>Web</em>. Add the Redirect URI above (add a <code>localhost</code> variant for development).</li>
              <li>Certificates &amp; secrets → new client secret; copy the value immediately.</li>
              <li>API permissions: delegated <code>openid</code>, <code>profile</code>, <code>email</code>.</li>
              <li>Paste the client ID + secret here and enable.</li>
            </ol>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default EntraSettings;
